/// <amd-module name="Feed/Legacy/Protocols/Npm/NpmConnectToFeedPanel" />
import * as React from "react";

import { Dropdown, IDropdownOption } from "OfficeFabric/Dropdown";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import * as Utils_Url from "VSS/Platform/Util/Url";

import { CiConstants, FwLinks } from "Feed/Common/Constants/Constants";
import { FeedView } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { ExternalLink } from "Feed/Legacy/Components/ExternalLink";
import { LabelWithExternalLink } from "Feed/Legacy/Components/LabelWithExternalLink";
import { PackageMessagePanel } from "Feed/Legacy/Components/PackageMessagePanel";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { isUserAnonymousOrPublic } from "Feed/Legacy/Helpers/PermissionHelper";
import { IConnectToFeedProps } from "Feed/Legacy/Protocols/Common/IConnectToFeedProps";
import { CopyableTextField, ICopyableTextFieldTelemetry } from "Feed/Legacy/Protocols/Components/CopyableTextField";
import { NpmCiConstants } from "Feed/Legacy/Protocols/Npm/Constants/NpmConstants";
import { INpmRestApiUrlGeneratorService } from "Feed/Legacy/Protocols/Npm/NpmRestApiUrlGeneratorService";
import { HubAction } from "Feed/Legacy/Types/IHubState";

export interface INpmConnectToFeedPanelProps extends IConnectToFeedProps {
    feedViews: FeedView[];
}

export interface INpmConnectToFeedPanelState {
    registryUrl: string;
    isAuthTokenLoading?: boolean;
}

export class NpmConnectToFeedPanel extends React.Component<INpmConnectToFeedPanelProps, INpmConnectToFeedPanelState> {
    private _generatedCredentials: CopyableTextField;
    private textfieldIds = ["npm-textfield-label1", "npm-textfield-label2", "npm-textfield-label3"];

    // best-effort to get the right platform newline. Could be tripped up by weird browsers,
    // but it's more important to be consistent on the page than correct.
    private newLine =
        window.navigator && window.navigator.platform && window.navigator.platform.toLowerCase().startsWith("win")
            ? "\r\n"
            : "\n";

    private showCredentialDetails: boolean;

    constructor(props: INpmConnectToFeedPanelProps) {
        super(props);

        const includeProject = this.props.feed && this.props.feed.project != null;
        const registryUrl = this.getRegistryUrl(props.pageContext, this._getFullyQualifiedFeedName(), includeProject);
        this.showCredentialDetails = isUserAnonymousOrPublic(this.props.pageContext, this.props.feed) == false;

        this.state = {
            registryUrl
        } as INpmConnectToFeedPanelState;

        CustomerIntelligenceHelper.publishEvent(this.props.pageContext, CiConstants.ConnectToFeed, {
            Action: CiConstants.ConnectToFeedTabChanged,
            Protocol: NpmCiConstants.ProtocolName,
            Context: HubAction[this.props.hubAction]
        });
    }

    public render(): JSX.Element {
        const viewOptions: IDropdownOption[] = [];
        viewOptions.push({
            key: PackageResources.FeedViewDropdown_AllViews_Key,
            text: PackageResources.FeedViewDropdown_ConnectToFeed_AllViews_Name
        } as IDropdownOption);

        if (this.props.feedViews) {
            this.props.feedViews.forEach(view => {
                viewOptions.push({
                    key: view.id,
                    text: "@" + view.name
                } as IDropdownOption);
            });
        }

        let registryText = "registry=" + this.state.registryUrl;
        if (this.showCredentialDetails) {
            registryText = registryText + this.newLine + "always-auth=true";
        }
        const isHosted = this.props.pageContext.getService<IVssPageService>("IVssPageService").getData().isHosted;

        return (
            <div className="npm-connect-to-feed-panel connect-to-feed-panel">
                {this.state.registryUrl ? (
                    <div className="connect-to-feed-section">
                        <div className="connect-to-feed-section-title" role="heading" aria-level={2}>
                            {PackageResources.NpmConnectControl_HeadingText}
                        </div>
                        <div className="connect-to-feed-views-dropdown">
                            <LabelWithExternalLink
                                pageContext={this.props.pageContext}
                                labelText={PackageResources.FeedViewDropdown_ConnectToFeed_Label}
                                linkText={PackageResources.FeedViewDropdown_ConnectToFeed_Label_Link}
                                href={FwLinks.ConnectToFeedViewWhatsThis}
                                ciContext={NpmCiConstants.NpmConnectToFeedPanel}
                            />
                            <Dropdown
                                className="views-dropdown"
                                ariaLabel={PackageResources.FeedViewDropdown_ConnectToFeed_Label}
                                options={viewOptions}
                                onChanged={option => this._onViewDropdownChanged(option)}
                                defaultSelectedKey={
                                    this.props.feed.view
                                        ? this.props.feed.view.id
                                        : PackageResources.FeedViewDropdown_AllViews_Key
                                }
                            />
                        </div>
                        <div className="connect-to-feed-textfield-container">
                            <div className="connect-to-feed-sub-heading" id={this.textfieldIds[0]}>
                                {PackageResources.NpmConnectControl_NpmrcProjectSubHeading}
                            </div>
                            <CopyableTextField
                                pageContext={this.props.pageContext}
                                text={registryText}
                                buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Npm_ProjectNpmrc_Copy}
                                textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Npm_ProjectNpmrc}
                                telemetryProperties={
                                    {
                                        commandName: CiConstants.CopyCommandAddSource,
                                        feedName: this.props.feed.name,
                                        protocol: NpmCiConstants.ProtocolName
                                    } as ICopyableTextFieldTelemetry
                                }
                                ariaDescribedByIdForLabelText={this.textfieldIds[0]}
                            />
                        </div>
                        {this.showCredentialDetails && this.renderCredentialDetails(isHosted)}
                        <ExternalLink
                            pageContext={this.props.pageContext}
                            className="connect-to-feed-link"
                            ciContext={NpmCiConstants.NpmConnectToFeedPanel}
                            href={PackageResources.NpmConnectControl_NpmrcDocLink}
                        >
                            {PackageResources.NpmConnectControl_NpmrcDocLinkText}
                        </ExternalLink>
                    </div>
                ) : (
                    <PackageMessagePanel message={PackageResources.ConnectControl_EndpointNotAvailable} />
                )}
            </div>
        );
    }

    private renderCredentialDetails(isHosted: boolean) {
        return (
            <div>
                {isHosted && (
                    <div>
                        <div className="connect-to-feed-sub-heading" id={this.textfieldIds[1]}>
                            <span className="connect-to-feed-sub-heading-bold">
                                {PackageResources.NpmConnectControl_WindowsUsers}
                            </span>
                            <span>{PackageResources.NpmConnectControl_AuthHelperSubHeading}</span>
                        </div>
                        <CopyableTextField
                            pageContext={this.props.pageContext}
                            text={PackageResources.NpmConnectControl_NpmInstallAuthHelperCommand}
                            buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Npm_InstallAuthHelper_Copy}
                            textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Npm_InstallAuthHelper}
                            telemetryProperties={
                                {
                                    commandName: NpmCiConstants.CopyCommandInstallAuth,
                                    feedName: this.props.feed.name,
                                    protocol: NpmCiConstants.ProtocolName
                                } as ICopyableTextFieldTelemetry
                            }
                            ariaDescribedByIdForLabelText={this.textfieldIds[1]}
                        />
                        <CopyableTextField
                            pageContext={this.props.pageContext}
                            text={PackageResources.NpmConnectControl_AuthHelperCommand}
                            buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Npm_RunAuthHelper_Copy}
                            textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Npm_RunAuthHelper}
                            telemetryProperties={
                                {
                                    commandName: NpmCiConstants.CopyCommandRunAuth,
                                    feedName: this.props.feed.name,
                                    protocol: NpmCiConstants.ProtocolName
                                } as ICopyableTextFieldTelemetry
                            }
                            ariaDescribedByIdForLabelText={this.textfieldIds[1]}
                        />
                    </div>
                )}
                <div className="connect-to-feed-sub-heading">
                    {isHosted && (
                        <span className="connect-to-feed-sub-heading-bold">
                            {PackageResources.NpmConnectControl_MacLinuxUsers}
                        </span>
                    )}
                    <span id={this.textfieldIds[2]}>{PackageResources.NpmConnectControl_AddCredentialsManually}</span>
                </div>
                <div>
                    <CopyableTextField
                        pageContext={this.props.pageContext}
                        ref={self => (this._generatedCredentials = self)}
                        text={this._getAuthTokenCommand()}
                        buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Npm_ManualCredentials_Copy}
                        textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Npm_ManualCredentials}
                        telemetryProperties={
                            {
                                commandName: CiConstants.CopyCommandAuthToken,
                                feedName: this.props.feed.name,
                                protocol: NpmCiConstants.ProtocolName
                            } as ICopyableTextFieldTelemetry
                        }
                        ariaDescribedByIdForLabelText={this.textfieldIds[2]}
                    />
                    <span className="warning-text">{PackageResources.Npm_WarningText_Head}</span>
                    <ExternalLink
                        pageContext={this.props.pageContext}
                        className="warning-text-link"
                        href="https://go.microsoft.com/fwlink/?LinkId=724423"
                        ciContext={NpmCiConstants.NpmConnectToFeedPanel}
                    >
                        {"npmrc"}
                    </ExternalLink>
                    <span className="warning-text">{PackageResources.Npm_WarningText_Body}</span>
                </div>
            </div>
        );
    }

    private _onViewDropdownChanged(option: IDropdownOption) {
        let feedName = this.props.feed.name;
        if (option.key !== PackageResources.FeedViewDropdown_AllViews_Key) {
            feedName = feedName + option.text;
        }

        const includeProject = this.props.feed && this.props.feed.project != null;
        const registryUrl = this.getRegistryUrl(this.props.pageContext, feedName, includeProject);
        this.setState({
            registryUrl
        });
    }

    private getRegistryUrl(pageContext: IVssPageContext, feedName: string, includeProject: boolean): string {
        const registryUrl: string = pageContext
            .getService<INpmRestApiUrlGeneratorService>("INpmRestApiUrlGeneratorService")
            .getRegistryUrl(feedName, includeProject);
        return registryUrl;
    }

    private _getFullyQualifiedFeedName(): string {
        if (this.props.feed.view) {
            return this.props.feed.name + "@" + this.props.feed.view.name;
        }
        return this.props.feed.name;
    }

    private _getBothRegistryEndpoints(uri: string): string[] {
        if (uri && uri.length > 0) {
            if (uri.charAt(uri.length - 1) === "/") {
                // endpoint has a slash, so remove it:
                return [uri, uri.substr(0, uri.length - 1)];
            }
        }
        // no ending slash:
        return [uri, uri + "/"];
    }

    private _setFocusToGeneratedCredentials() {
        if (this._generatedCredentials) {
            this._generatedCredentials.focus();
        }
    }

    private _getAuthTokenCommand() {
        let registryEndPoints = this._getBothRegistryEndpoints(this.state.registryUrl);

        const newlineRegex = /\r\n|\r|\n/;

        // split the templates at newlines, so we can ensure newlines are normalized in the generated output
        const authTokenHeaderLines = PackageResources.NpmConnectControl_AuthTokenHeader.split(newlineRegex);
        const authTokenLineTemplates = PackageResources.NpmConnectControl_AuthTokenStub.split(newlineRegex);
        const authTokenFooterLines = PackageResources.NpmConnectControl_AuthTokenFooter.split(newlineRegex);

        function formatHeaderFooter(template: string): string {
            return Utils_String.format(template, "____");
        }

        const authTokenLines = [];
        for (const headerLineTemplate of authTokenHeaderLines) {
            authTokenLines.push(formatHeaderFooter(headerLineTemplate));
        }

        for (const endpoint of registryEndPoints) {
            const authUrl = NpmConnectToFeedPanel._getNpmAuthUrl(endpoint);
            for (const authTokenLineTemplate of authTokenLineTemplates) {
                const tokenLine = Utils_String.format(
                    authTokenLineTemplate,
                    authUrl,
                    "[INSERT BASE64-ENCODED PAT HERE]",
                    this.props.feed.name,
                    PackageResources.NpmConnectControl_Email
                );
                authTokenLines.push(tokenLine);
            }
        }

        for (const footerLineTemplate of authTokenFooterLines) {
            authTokenLines.push(formatHeaderFooter(footerLineTemplate));
        }

        // rejoin the lines with a single consistent newline character
        return authTokenLines.join(this.newLine);
    }

    public static _getNpmAuthUrl(registryEndpointUrl: string) {
        const uri = new Utils_Url.Uri(registryEndpointUrl);
        delete uri.scheme;
        delete uri.queryParameters;
        delete uri.hashString;

        const lastSlashIndex = uri.path.lastIndexOf("/");
        // include everything up to the last slash, discard everything after it (including the slash):
        const path = lastSlashIndex > 0 ? "/" + uri.path.substr(0, lastSlashIndex) : "";

        // absoluteUri encodes the path, which means it won't match the registry uri property. We'll append it ourselves.
        delete uri.path;

        // this is mimicking NPM's 'toNerfDart' behavior.  The final slash is added by the string formatter above:
        return "//" + Utils_Url.combineUrlPaths(uri.absoluteUri, path);
    }
}
